import type {
  ChangeEvent as ReactChangeEvent,
  MouseEvent as ReactMouseEvent,
} from "react";
import { action, computed, makeObservable, observable } from "mobx";
import { injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";
import type { BoxReferenceInputOption, BoxReferenceInputProps } from "./BoxReferenceInput";
import {
  OverlayEffects,
  matchesViewportQuery,
} from "@/components/shared/viewmodel/overlay-effects";
import {
  filterBoxReferenceOptions,
  findSelectedBoxReferenceOption,
  getBoxReferenceCountLabel,
  getBoxReferenceEmptyStateText,
  isBoxReferenceOptionSelected,
} from "./viewmodel/options";

const MOBILE_QUERY = "(max-width: 700px)";

let inputIdSeed = 0;

@injectable()
export class BoxReferenceInputViewModel extends ViewModel<
  unknown,
  BoxReferenceInputProps
> {
  private readonly uid = (inputIdSeed += 1);

  private rootElement: HTMLDivElement | null = null;

  private readonly keyDownHandler = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && this.isOpen) {
      this.close();
    }
  };

  private readonly documentMouseDownHandler = (event: MouseEvent): void => {
    if (!this.isOpen || !this.rootElement) {
      return;
    }

    const target = event.target;
    if (target instanceof Node && this.rootElement.contains(target)) {
      return;
    }

    this.close();
  };

  private readonly overlayEffects = new OverlayEffects(
    this.keyDownHandler,
    this.documentMouseDownHandler,
  );

  @observable
  isOpen = false;

  @observable
  searchValue = "";

  constructor() {
    super();
    makeObservable(this);
  }

  @computed
  get value(): string { return this.viewProps.value; }

  @computed
  get options(): BoxReferenceInputOption[] { return this.viewProps.options; }

  @computed
  get placeholder(): string { return this.viewProps.placeholder || "Выберите коробку"; }

  @computed
  get searchPlaceholder(): string { return this.viewProps.searchPlaceholder || "Найти коробку"; }

  @computed
  get disabled(): boolean { return this.viewProps.disabled ?? false; }

  @computed
  get isLoading(): boolean { return this.viewProps.isLoading ?? false; }

  @computed
  get allowEmpty(): boolean { return this.viewProps.allowEmpty ?? false; }

  @computed
  get emptyLabel(): string { return this.viewProps.emptyLabel || "Без родительской коробки"; }

  @computed
  get selectedOption(): BoxReferenceInputOption | null {
    return findSelectedBoxReferenceOption(this.options, this.value) || null;
  }

  @computed
  get displayValue(): string {
    return this.value.trim() ? this.selectedOption?.label || this.value.trim() : this.placeholder;
  }

  @computed
  get hasValue(): boolean { return this.value.trim().length > 0; }

  @computed
  get filteredOptions(): BoxReferenceInputOption[] {
    return filterBoxReferenceOptions(this.options, this.searchValue);
  }

  @computed
  get hasFilteredOptions(): boolean { return this.filteredOptions.length > 0; }

  @computed
  get emptyStateText(): string {
    return getBoxReferenceEmptyStateText({ isLoading: this.isLoading, optionsCount: this.options.length });
  }

  @computed
  get countLabel(): string {
    return getBoxReferenceCountLabel({
      isLoading: this.isLoading, searchValue: this.searchValue,
      optionsCount: this.options.length, filteredCount: this.filteredOptions.length,
    });
  }

  get titleId(): string { return `box-reference-title-${this.uid}`; }

  get listId(): string { return `box-reference-list-${this.uid}`; }

  protected onViewMounted(): void { this.syncEffects(); }

  protected onViewUpdated(): void { this.syncEffects(); }

  protected onViewUnmounted(): void { this.overlayEffects.reset(); }

  @action.bound
  setRootRef(element: HTMLDivElement | null): void { this.rootElement = element; }

  @action.bound
  open(): void {
    if (this.disabled) { return; }
    this.searchValue = "";
    this.isOpen = true;
  }

  @action.bound
  close(): void { this.searchValue = ""; this.isOpen = false; }

  @action.bound
  toggle(): void {
    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  @action.bound
  onSearchChange(event: ReactChangeEvent<HTMLInputElement>): void { this.searchValue = event.target.value; }

  @action.bound
  onLayerMouseDown(): void { this.close(); }

  @action.bound
  onPanelMouseDown(event: ReactMouseEvent<HTMLDivElement>): void { event.stopPropagation(); }

  @action.bound
  onEmptySelect(): void {
    if (!this.allowEmpty) { return; }
    this.viewProps.onValueChange("");
    this.close();
  }

  @action.bound
  selectOption(option: BoxReferenceInputOption): void {
    if (this.disabled) { return; }
    this.viewProps.onValueChange(option.label);
    this.close();
  }

  isOptionSelected(option: BoxReferenceInputOption): boolean {
    return isBoxReferenceOptionSelected(option, this.value);
  }

  private syncEffects(): void {
    this.overlayEffects.sync({
      keyDown: this.isOpen,
      documentMouseDown: this.isOpen,
      scrollLock: this.isOpen && matchesViewportQuery(MOBILE_QUERY),
    });
  }
}
